import React, { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';

import { useConfig } from '@occc/config';
import { getAudiencePageHeaderLinks } from './utils';

import { AudienceDetails } from './AudienceDetails';
import { AudienceName } from './AudienceName';
import { AudienceActionsDropdown } from './AudienceActionsDropdown';

import { Audience } from '../types';

interface AudienceDetailsPageProps {
  loading: boolean;
  error?: ApolloError;
  audience?: Audience;
  onActivate?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onExport?: () => void;
  isUserPermittedToEditAudience: boolean;
  isUserPermittedToExportAudience: boolean;
  isUserPermittedToArchiveAudience: boolean;
  isUserPermittedToActivateAudience: boolean;
}

export const AudienceDetailsPage: React.FC<AudienceDetailsPageProps> = ({
  loading,
  audience,
  error,
  onActivate,
  onArchive,
  onUnarchive,
  onExport,
  isUserPermittedToEditAudience,
  isUserPermittedToExportAudience,
  isUserPermittedToArchiveAudience,
  isUserPermittedToActivateAudience,
}) => {
  const { enabled: isExportEnabled } = useConfig('audienceReportExport');
  const links = useMemo(
    () => getAudiencePageHeaderLinks({ audienceName: audience?.name || 'Audience details' }),
    [audience?.name],
  );

  useAppLayoutContextConsumer(links);

  return (
    <Page
      browserTabTitle='Audience'
      actions={
        <AudienceActionsDropdown
          audience={audience}
          loading={loading}
          error={error}
          onActivate={onActivate}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onExport={onExport}
          isEditAvailable={isUserPermittedToEditAudience}
          isExportAvailable={isExportEnabled && isUserPermittedToExportAudience}
          isArchiveAvailable={isUserPermittedToArchiveAudience}
          isActivateAvailable={isUserPermittedToActivateAudience}
        />
      }
      pageHeaderProps={{
        title: (
          <AudienceName loading={loading} error={error} audience={audience} name={audience?.name} />
        ),
        subtitle: audience?.description,
      }}
    >
      <AudienceDetails loading={loading} audience={audience} error={error} />
    </Page>
  );
};
